import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { consultantWa, formatPhone } from "@/data/team";
import { WaIcon } from "@/components/ui/WaIcon";
import { EASE } from "@/components/ui/motion";

const frame = {
  hidden: { clipPath: "inset(100% 0% 0% 0% round 22px 72px 22px 22px)" },
  show: { clipPath: "inset(0% 0% 0% 0% round 22px 72px 22px 22px)", transition: { duration: 1.1, ease: EASE } },
};
const img = { hidden: { scale: 1.3 }, show: { scale: 1, transition: { duration: 1.6, ease: EASE } } };

const Photo = ({ c }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 160, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 160, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1000px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="will-change-transform">
        <Link to={`/konsultan/${c.slug}`} className="group/photo relative block" data-testid={`consultant-profile-${c.slug}`}>
          <motion.div variants={frame} className="relative aspect-[3/4] overflow-hidden bg-ice">
            <motion.img variants={img} src={c.photo} alt={`Foto ${c.name}, ${c.role}`} loading="lazy" draggable="false" className="h-full w-full object-cover object-top transition-[filter] duration-700 group-hover/photo:saturate-[1.15]" />
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/45 to-transparent transition-transform duration-[1.1s] ease-[cubic-bezier(.22,1,.36,1)] group-hover/photo:translate-x-full" aria-hidden="true" />
            <span className="absolute inset-0 bg-gradient-to-t from-[#263746]/85 via-[#263746]/20 to-transparent" aria-hidden="true" />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0284C7]/70 to-transparent opacity-0 transition-opacity duration-700 group-hover/photo:opacity-100" aria-hidden="true" style={{ top: "40%" }} />
            <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slateb backdrop-blur">{c.role.replace("Senior ", "Sr. ")}</span>
            <span className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
              <span className="block font-display text-[17px] font-bold leading-tight text-white transition-transform duration-500 group-hover/photo:-translate-y-1">{c.name}</span>
              <span className="mt-1 flex items-center gap-1.5 text-[12px] font-semibold text-sky-100 opacity-0 transition-[opacity,transform] duration-500 group-hover/photo:opacity-100 [transform:translateY(8px)] group-hover/photo:[transform:translateY(0)]">
                Lihat profil <ArrowRight size={12} />
              </span>
            </span>
          </motion.div>
        </Link>
      </motion.div>
    </div>
  );
};

export const ConsultantCard = ({ c, className = "", ...rest }) => (
  <motion.li initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className={`list-none ${className}`} data-testid={`consultant-card-${c.slug}`} {...rest}>
    <Photo c={c} />
    <a
      href={consultantWa(c)}
      target="_blank"
      rel="noopener noreferrer"
      className="group/wa mt-3 flex items-center gap-2 rounded-full border border-line bg-white py-1.5 pl-1.5 pr-3 transition-[border-color,background-color] duration-300 hover:border-[#25D366] hover:bg-[#25D366]/10 sm:gap-2.5 sm:pr-4"
      data-testid={`consultant-wa-${c.slug}`}
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#25D366] text-white transition-transform duration-500 group-hover/wa:rotate-[-12deg] group-hover/wa:scale-110 sm:h-8 sm:w-8">
        <WaIcon size={16} />
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-soft">Chat {c.name.split(" ")[0]}</span>
        <span className="block whitespace-nowrap text-[12px] font-semibold tracking-tight text-slateb sm:text-[13px]" data-testid={`consultant-phone-${c.slug}`}>{formatPhone(c.wa)}</span>
      </span>
    </a>
  </motion.li>
);
